import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { orgDashboardApiIsomorphicFetchListingApplicationsWithPagination } from '#/modules/orgDashboard/api/listingApplication/orgDashboardApiFetchListingApplicationsWithPagination';
import { orgDashboardApiIsomorphicFetchOrg } from '#/modules/orgDashboard/api/orgDashboardApiFetchOrg';
import { orgDashboardViewAllVolunteersRoute } from '#/modules/orgDashboard/routing/orgDashboard.routes';
import type { OrgDashboardOrgSubLayoutHandleProps } from '#/modules/orgDashboard/sub-layouts/Org/OrgDashboardOrgSubLayoutHandleProps';
import { NotFoundPageLoadable } from '#/pages/NotFound/NotFoundPageLoadable';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { getAppRouteSearchParams } from '#/routing/utils/getAppRouteSearchParams';

import { OrgDashboardViewAllVolunteersPageLoadable } from './OrgDashboardViewAllVolunteersPageLoadable';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';

export const handle = {
  fallbackUrlByLocaleToCurrent: true,
  darkBackground: true,
  showDesktopSearch: true,
} satisfies LayoutHandleProps & OrgDashboardOrgSubLayoutHandleProps;

export async function loader({
  request,
  context,
  params: { orgId },
}: HorizonAppRouteLoaderArgs<typeof orgDashboardViewAllVolunteersRoute>) {
  const searchParams = getAppRouteSearchParams({
    url: request.url,
    route: orgDashboardViewAllVolunteersRoute,
  });

  const pageIndex = parseInt(searchParams.page ?? '1', 10);

  const [{ org }, listingApplicationsResponse] = await Promise.all([
    orgDashboardApiIsomorphicFetchOrg({
      request,
      context,
    })(orgId),
    orgDashboardApiIsomorphicFetchListingApplicationsWithPagination({
      request,
      context,
    })(orgId, {
      withPagination: true,
      listingType: 'VOLOP',
      page: pageIndex - 1,
      sortBy: searchParams.sortBy ?? undefined,
      sortOrder: searchParams.sortOrder ?? undefined,
    }),
    OrgDashboardViewAllVolunteersPageLoadable.load(),
  ]);

  if (!org.membership.isActive) {
    await NotFoundPageLoadable.load();
    return idealistNotFound(null);
  }

  return {
    listingApplications: listingApplicationsResponse.listingApplications,
    listingApplicationsMeta:
      listingApplicationsResponse.listingApplicationsMeta,
  };
}

export default function OrgDashboardViewAllVolunteersHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  if (!loaderData) return <NotFoundPageLoadable />;

  return (
    <RouteModule title={i18n('View All Volunteers')}>
      <OrgDashboardViewAllVolunteersPageLoadable
        listingApplications={loaderData.listingApplications}
        listingApplicationsMeta={loaderData.listingApplicationsMeta}
      />
    </RouteModule>
  );
}
